import React, { useMemo, useState } from 'react';

import { Autocomplete, Paper, Popper, Skeleton, styled, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import { Error, StyledSubtext } from '../../../../../components/forms/styles';
import ClassContainer from '../../ClassContainer';
import { StyledFieldsetInstructors, StyledInputContainer } from '../index.styles';

const StyledListItem = styled('li')`
    span:first-of-type {
        margin-right: auto;
    }
`;

const LoadingListItem = styled('div')`
    padding: 0 10px;
`;

interface AdditionalInstructorOption {
    email: string;
    user_name: string;
    loading: boolean;
}

const Instructors: React.FC = () => {
    const { t } = useTranslation('create_edit_class');
    const { t: tCalendar } = useTranslation('calendar');

    const [currentValue, setCurrentValue] = useState('');

    const { control, formActionType, instructors, setValue, instructorsStatus } = useContainer(ClassContainer);
    const autocompleteOptions = useMemo(() => {
        if (instructorsStatus === 'LOADING') {
            const options: { loading: boolean; email: string; user_name: string; }[] = [];

            for (let i = 1; i <= 8; i++) {
                options.push({ loading: true, email: '', user_name: '' });
            }

            return options;
        }

        return instructors.map((instructor) => ({ loading: false, email: instructor.email, user_name: instructor.user_name }));
    }, [instructors, instructorsStatus]);

    if (formActionType === 'CREATE') {
        return (
            <StyledFieldsetInstructors>
                <StyledInputContainer>
                    <Controller
                        name="instructorEmails"
                        control={control}
                        render={({ field, fieldState }) => (
                            <Autocomplete
                                multiple={true}
                                freeSolo={true}
                                onChange={(_, values) => {
                                    const emails = values.map((value) => (typeof value === 'string' ? value.trim() : value.email));
                                    setCurrentValue('');
                                    field.onChange(emails);
                                }}
                                value={field.value}
                                options={autocompleteOptions}
                                getOptionLabel={(value) => (typeof value === 'string' ? value : value.email)}
                                PaperComponent={(props) => <Paper {...props} elevation={4} />}
                                PopperComponent={(props) => <Popper {...props} placement="top-start" modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]} />}
                                renderOption={(props, option) => {
                                    if (option.loading) {
                                        return (
                                            <LoadingListItem key={props.id}>
                                                <Skeleton variant="text" animation="wave" width="100%" height={30} />
                                            </LoadingListItem>
                                        );
                                    }

                                    return (
                                        <StyledListItem {...props} key={props.id}>
                                            <span>{option.user_name}</span>
                                            <span>{option.email}</span>
                                        </StyledListItem>
                                    );
                                }}
                                filterSelectedOptions={true}
                                filterOptions={(options) => {
                                    // Note: Avoid using the filter function here for better performance
                                    // (Array may contain 5000+ instructors)
                                    const filteredInstructors: AdditionalInstructorOption[] = [];

                                    options.forEach((instructor) => {
                                        const lowercasedEmail = instructor.email.toLowerCase();
                                        const lowercasedUsername = instructor.user_name.toLowerCase();

                                        const matchesEmail = lowercasedEmail.includes(currentValue.trim());
                                        const matchesUsername = lowercasedUsername.includes(currentValue.trim());

                                        if (matchesEmail || matchesUsername) {
                                            filteredInstructors.push({ email: instructor.email, user_name: instructor.user_name, loading: instructor.loading });
                                        }
                                    });

                                    return filteredInstructors.slice(0, 100);
                                }}
                                inputValue={currentValue}
                                renderInput={(params) => (
                                    <div>
                                        <TextField
                                            {...params}
                                            name="instructorEmails"
                                            label={t('advanced.additional-instructors')}
                                            inputProps={params.inputProps}
                                            ref={params.InputProps.ref}
                                            onChange={(e) => {
                                                const { value } = e.target;

                                                const delimiters = [';', ' ', ','];
                                                const shouldAddNewValue = delimiters.some((delimiter) => value.length > 2 && value.endsWith(delimiter));

                                                if (shouldAddNewValue) {
                                                    const email = value.slice(0, -1);
                                                    const values = [...field.value, email];

                                                    setCurrentValue('');
                                                    field.onChange(values);
                                                    setValue('instructorEmails', values);
                                                } else {
                                                    setCurrentValue(value.trim());
                                                }
                                            }}
                                            error={!!fieldState.error?.message}
                                            aria-describedby="additional-instructors-helper-text"
                                            placeholder={field.value.length === 0 ? t('advanced.additional-instructors-placeholder') : ''}
                                            onBlur={(e) => {
                                                const values = [...field.value];
                                                const email = e.target.value.trim();

                                                if (email && !values.includes(email)) {
                                                    values.push(email);
                                                }

                                                setCurrentValue('');
                                                field.onChange(values);
                                                setValue('instructorEmails', values);
                                            }}
                                        />
                                        {fieldState.error?.message ? <Error>{fieldState.error?.message}</Error> : <StyledSubtext id="additional-instructors-helper-text">{tCalendar('optional')}</StyledSubtext>}
                                    </div>
                                )}
                            />

                        )}
                    />
                </StyledInputContainer>
            </StyledFieldsetInstructors>
        );
    }

    return null;
};

export default Instructors;
